/**
 * assetCache.js — Persist image bytes for offline remounts.
 *
 * Webpack SVG/PNG URLs and remote org logos are loaded via <img src>. After
 * the rating screen unmounts (thank-you → reset) the browser often refetches
 * them. That fails offline when HTTP cache is cold, expired, or disabled
 * (DevTools "Disable cache", mobile eviction). Store blobs in IndexedDB on
 * first successful fetch and reuse object URLs afterwards.
 */

import React, { useEffect, useState } from "react";

const DB_NAME = "VestaOfflineAssetCache";
const DB_VERSION = 1;
const STORE_NAME = "images";
const CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

let _db = null;
const memoryUrls = new Map(); /* url -> object URL */

const openDB = () =>
    new Promise((resolve, reject) => {
        if (_db) return resolve(_db);
        const req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = (e) => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: "key" });
            }
        };
        req.onsuccess = (e) => {
            _db = e.target.result;
            _db.onversionchange = () => {
                _db?.close?.();
                _db = null;
            };
            _db.onclose = () => {
                _db = null;
            };
            resolve(_db);
        };
        req.onerror = (e) => reject(e.target.error);
    });

const isCacheableSrc = (src) => {
    if (!src || typeof src !== "string") return false;
    if (src.startsWith("data:") || src.startsWith("blob:")) return false;
    return true;
};

const idbPut = async (key, blob) => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        tx.objectStore(STORE_NAME).put({ key, blob, ts: Date.now() });
        tx.oncomplete = () => resolve();
        tx.onerror = (e) => reject(e.target.error);
    });
};

const idbGet = async (key) => {
    const db = await openDB();
    return new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readonly");
        const req = tx.objectStore(STORE_NAME).get(key);
        req.onsuccess = () => {
            const entry = req.result;
            if (!entry?.blob) return resolve(null);
            if (Date.now() - (entry.ts ?? 0) > CACHE_TTL_MS) return resolve(null);
            resolve(entry.blob);
        };
        req.onerror = (e) => reject(e.target.error);
    });
};

const blobToObjectUrl = (src, blob) => {
    if (memoryUrls.has(src)) return memoryUrls.get(src);
    const objectUrl = URL.createObjectURL(blob);
    memoryUrls.set(src, objectUrl);
    return objectUrl;
};

/**
 * Fetch an image (same-origin or CORS) and keep the bytes for offline use.
 */
export const rememberImage = async (src) => {
    if (!isCacheableSrc(src) || memoryUrls.has(src)) return;
    try {
        const cached = await idbGet(src).catch(() => null);
        if (cached) {
            blobToObjectUrl(src, cached);
            return;
        }
        const res = await fetch(src, { mode: "cors", credentials: "omit" });
        if (!res.ok) return;
        const blob = await res.blob();
        if (!blob || blob.size === 0) return;
        await idbPut(src, blob).catch(() => {});
        blobToObjectUrl(src, blob);
    } catch {
        /* CORS / offline / quota — leave the original src in place */
    }
};

export const rememberImages = (srcs) => {
    (srcs || []).forEach((src) => {
        rememberImage(src).catch(() => {});
    });
};

/**
 * Return a src that works offline if we already cached the bytes.
 */
export const resolveImageSrc = async (src) => {
    if (!isCacheableSrc(src)) return src;
    if (memoryUrls.has(src)) return memoryUrls.get(src);
    try {
        const blob = await idbGet(src);
        if (blob) return blobToObjectUrl(src, blob);
    } catch {
        /* ignore */
    }
    rememberImage(src).catch(() => {});
    return src;
};

/**
 * Drop-in <img> that prefers a cached blob URL after the first online load.
 */
export const CachedImg = ({ src, alt = "", ...props }) => {
    const [resolved, setResolved] = useState(src);

    useEffect(() => {
        let live = true;
        if (!src) {
            setResolved(src);
            return undefined;
        }
        setResolved(src);
        resolveImageSrc(src).then((next) => {
            if (live && next) setResolved(next);
        });
        return () => {
            live = false;
        };
    }, [src]);

    if (!src) return null;
    return <img src={resolved || src} alt={alt} {...props} />;
};
